import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useQuery, useQueryClient } from 'react-query';
import { getAllOrgInvoices, getOrgSubscriptionDetails, ApiInvoice } from '../../../../helpers/orgSubscriptionHelper';
import { ORG_INVOICES_LIST, ORG_SUBSCRIPTION_DETAILS } from '../../../../constants/queryKeys';
import logger from '../../../../utils/logger';
import { useTranslation } from '../../../../hooks/useTranslation';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import { _ToastMessageProps } from '../../../../constants/staticTypes';
import { defaultToastData } from '../../../../helpers/commonHelper';
import {
    INVOICE_HISTORY,
    LOADING_INVOICES,
    NO_INVOICES_FOUND,
    RENEWS_IN_X_DAYS,
    CURRENT_PLAN,
    FAILED_TO_FETCH_INVOICES,
} from '../../../../constants/strings';
import './OrgSubscription.scss';

const languageTexts = {
    INVOICE_HISTORY,
    LOADING_INVOICES,
    NO_INVOICES_FOUND,
    RENEWS_IN_X_DAYS,
    CURRENT_PLAN,
    FAILED_TO_FETCH_INVOICES,
};

interface InvoiceHistoryProps {
    onBack: () => void;
}

const InvoiceHistory: React.FC<InvoiceHistoryProps> = ({ onBack }) => {
    const queryClient = useQueryClient();
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState<number>(currentYear);
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const translatedText = useTranslation(languageTexts, 'none');

    // Use cached subscription details (already fetched by Overview)
    const { data: subscriptionResponse } = useQuery({
        queryKey: [ORG_SUBSCRIPTION_DETAILS],
        queryFn: () => getOrgSubscriptionDetails(queryClient),
        refetchOnWindowFocus: false,
        retry: false,
    });
    const subApiData = subscriptionResponse?.data?.data;
    const plan = subApiData?.organizationProductPlan?.plan;
    const currentPrice = subApiData?.organizationProductPlan?.price;

    const planName = plan?.name || 'No Subscription Added';
    const planDescription = plan?.description || '';
    const planPrice = subApiData?.nextAmountPayableInDollars ?? (currentPrice?.amount ? parseFloat(currentPrice.amount) : 0);
    const billingInterval = currentPrice?.recurring?.interval === 'year' ? 'year' : 'month';
    const periodLabel = billingInterval === 'year' ? 'Per year' : 'Per month';

    // Days until next billing
    const nextBillingDate = subApiData?.nextBillingDate ? new Date(subApiData.nextBillingDate) : null;
    const renewsIn = nextBillingDate ? Math.max(Math.ceil((nextBillingDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24)), 0) : null;

    // Fetch invoices from API
    const {
        data: invoicesData,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: [ORG_INVOICES_LIST],
        queryFn: () => getAllOrgInvoices(queryClient),
        onError: (error) => {
            logger.error('Failed to fetch invoices', error);
            setToastMessage({
                message: translatedText.FAILED_TO_FETCH_INVOICES || FAILED_TO_FETCH_INVOICES,
                variant: 'error',
                show: true,
            });
        },
        refetchOnWindowFocus: false,
    });

    // Generate years array dynamically (current year and previous 2 years)
    const years = Array.from({ length: 3 }, (_, i) => {
        const year = currentYear - i;
        return { label: year.toString(), value: year };
    });

    // Filter invoices by selected year
    const invoices: ApiInvoice[] = invoicesData ?? [];
    const filteredInvoices = invoices.filter((invoice) => {
        if (!invoice.created_at) return false;
        const invoiceYear = new Date(invoice.created_at).getFullYear();
        return invoiceYear === selectedYear;
    });

    // Format date for display
    const formatDate = (dateString: string | null): string => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const handleViewInvoice = (invoice: ApiInvoice) => {
        if (invoice.hosted_invoice_url) {
            window.open(invoice.hosted_invoice_url, '_blank');
        } else {
            logger.log('No hosted invoice URL available');
        }
    };

    const handleDownloadInvoice = (invoice: ApiInvoice) => {
        if (invoice.invoice_pdf) {
            window.open(invoice.invoice_pdf, '_blank');
        } else {
            logger.log('No invoice PDF available');
        }
    };

    return (
        <div className="invoice-history-container">
            {/* Header */}

            <div className="invoice-history-content">
                {/* Invoice List */}
                <div className="invoice-list-section">
                    <div className="invoice-history-header w-100 d-flex align-items-center justify-content-between">
                        <div className="header-left">
                            <Button icon="ri-arrow-left-line" className="back-btn" text aria-label="Back to overview" onClick={onBack} />
                            <h2>{translatedText.INVOICE_HISTORY || INVOICE_HISTORY}</h2>
                        </div>
                        <Dropdown
                            value={selectedYear}
                            options={years}
                            onChange={(e) => setSelectedYear(e.value)}
                            className="year-dropdown"
                        />
                    </div>
                    {isLoading ? (
                        <div className="loading-state">
                            <i className="ri-loader-4-line rotating-icon" />
                            <p>{translatedText.LOADING_INVOICES || LOADING_INVOICES}</p>
                        </div>
                    ) : filteredInvoices.length > 0 ? (
                        <div className="invoice-list">
                            {filteredInvoices.map((invoice) => (
                                <div key={invoice.invoice_id} className="invoice-item">
                                    <div className="invoice-info">
                                        <i className="ri-file-pdf-2-fill invoice-icon" />
                                        <span className="invoice-name">{invoice.invoice_id}</span>
                                    </div>
                                    <div className="invoice-actions">
                                        <span className="invoice-date">{formatDate(invoice.created_at)}</span>
                                        <Button
                                            icon="ri-eye-line"
                                            className="action-btn"
                                            text
                                            rounded
                                            aria-label="View invoice"
                                            onClick={() => handleViewInvoice(invoice)}
                                            disabled={!invoice.hosted_invoice_url}
                                        />
                                        <Button
                                            icon="ri-download-line"
                                            className="action-btn download-btn"
                                            text
                                            rounded
                                            aria-label="Download invoice"
                                            onClick={() => handleDownloadInvoice(invoice)}
                                            disabled={!invoice.invoice_pdf}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="no-invoices">
                            <p>{(translatedText.NO_INVOICES_FOUND || NO_INVOICES_FOUND).replace('{0}', selectedYear.toString())}</p>
                        </div>
                    )}
                </div>

                {/* Current Plan Card */}
                <div className="current-plan-card">
                    <div className="plan-badge">
                        <span className="badge-text">{planName}</span>
                        {renewsIn !== null && (
                            <span className="renews-badge">
                                {(translatedText.RENEWS_IN_X_DAYS || RENEWS_IN_X_DAYS).replace('{0}', renewsIn.toString())}
                            </span>
                        )}
                    </div>
                    <p className="plan-description">{planDescription}</p>
                    <div className="plan-pricing">
                        <span className="price">${planPrice.toFixed(2)}</span>
                        <span className="period">{periodLabel}</span>
                    </div>
                    <div className="current-plan-indicator">{translatedText.CURRENT_PLAN || CURRENT_PLAN}</div>
                </div>
            </div>

            <MlxToast
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
        </div>
    );
};

export default InvoiceHistory;
